// src/modules/app-views/invoices/pages/Init/modals/FiscalBootstrapModal/Step4.jsx
// *******************************************************************************
// STEP 4 - NFCE CONFIGURATION
// *******************************************************************************
// Fourth step: Configure and activate NFCe service. User must enable NFCe
// successfully to complete the bootstrap flow.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import { Alert } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { openModal } from 'components/core/GenericModal';
import NfceCard from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/NfceCard';
import CscTokenModal from 'modules/app-views/account/pages/Subscription/modals/CscTokenModal';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import fiscalService from 'services/fiscalService';
import { API } from 'constants/Api';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Fields required to activate NFCe service
const NFCE_REQUIRED_FIELDS = [
  { path: ['fiscalData', 'nfce', 'provider'], messageKey: 'inform_nfce_provider' },
  { path: ['fiscalData', 'nfce', 'environment'], messageKey: 'inform_nfce_environment' },
  { path: ['fiscalData', 'nfce', 'currentSerie'], messageKey: 'inform_nfce_serie' },
  { path: ['fiscalData', 'nfce', 'csc', 'id'], messageKey: 'inform_nfce_csc_id' },
  {
    path: ['fiscalData', 'nfce', 'csc', 'token'],
    altPath: ['fiscalData', 'nfce', 'csc', 'tokenMasked'],
    messageKey: 'inform_nfce_csc_token',
  },
];

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step4Content = ({ entityId, company, nfceEnabled, onNfceEnabled, form }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const entityUf = company?.address?.region || null;
  const cscLinkUrl = fiscalService.getCscLinks(entityUf, 'url');
  const cscLinkName = fiscalService.getCscLinks(entityUf, 'name');

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [localNfceEnabled, setLocalNfceEnabled] = useState(nfceEnabled);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync with prop
  useEffect(() => {
    setLocalNfceEnabled(nfceEnabled);
  }, [nfceEnabled]);

  // Populate form with company NFCe data
  useEffect(() => {
    if (form && company) {
      const nfceData = company?.fiscalData?.nfce || {};

      form.setFieldsValue({
        fiscalData: {
          nfce: {
            provider: nfceData.provider || 'pachamama',
            environment: nfceData.environment || 'homologation',
            currentSerie: nfceData.currentSerie || 1,
            lastNumber: nfceData.lastNumber || 0,
            csc: {
              id: nfceData.csc?.id || '',
              tokenMasked: nfceData.csc?.tokenMasked || '',
            },
            isEnabled: nfceData.isEnabled || false,
          },
        },
      });
    }
  }, [form, company]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleNfceToggle = useCallback(
    async checked => {
      setValidationError(null);

      // If turning OFF
      if (!checked) {
        setLocalNfceEnabled(false);
        onNfceEnabled?.(false);
        if (form) {
          form.setFieldValue(['fiscalData', 'nfce', 'isEnabled'], false);
        }
        appService.message({ flag: 's', text: 'service_deactivated', duration: 2 });
        return;
      }

      // If turning ON - validate first
      if (!entityId) {
        appService.notification({
          flag: 'error',
          title: 'error',
          description: 'entity_not_found',
        });
        return;
      }

      const msg = await appService.message({
        flag: 'loading',
        text: 'validating_nfce_data',
        duration: 0,
      });

      setValidating(true);

      // Validate required fields
      const emptyFields = [];
      NFCE_REQUIRED_FIELDS.forEach(({ path, altPath, messageKey }) => {
        const value = form?.getFieldValue(path);
        const isEmpty = value === undefined || value === null || value === '';

        let altIsEmpty = true;
        if (isEmpty && altPath) {
          const altValue = form?.getFieldValue(altPath);
          altIsEmpty = altValue === undefined || altValue === null || altValue === '';
        }

        if (isEmpty && altIsEmpty) {
          emptyFields.push({ name: path, messageKey });
        }
      });

      if (emptyFields.length > 0) {
        const firstEmptyField = emptyFields[0];
        msg.update({
          flag: 'warning',
          text: firstEmptyField.messageKey,
          duration: 4,
        });

        const fieldsToMark = emptyFields.map(({ name }) => ({
          name,
          errors: [translateX('required')],
        }));
        form.setFields(fieldsToMark);

        setValidating(false);
        return;
      }

      // Backend validation
      try {
        const formValues = form?.getFieldsValue(true) || {};
        const nfceData = formValues?.fiscalData?.nfce || {};

        const payload = {
          entityId,
          service: 'nfce',
          provider: nfceData.provider,
          fiscalData: {
            nfce: {
              environment: nfceData.environment,
              currentSerie: nfceData.currentSerie,
              csc: {
                id: String(nfceData.csc?.id || ''),
                token: nfceData.csc?.token || '',
              },
            },
          },
        };

        const [response] = await axiosService.post({
          url: API.FISCAL.SETUP_VALIDATE_SERVICE,
          data: payload,
          returnAsObject: true,
        });

        if (response?.isValid) {
          setLocalNfceEnabled(true);
          onNfceEnabled?.(true);
          if (form) {
            form.setFieldValue(['fiscalData', 'nfce', 'isEnabled'], true);
          }

          msg.update({
            flag: 'success',
            text: 'service_enabled',
            duration: 3,
          });
        } else {
          const errors = response?.errorList || [];
          const errorMessages = errors.map(e => translateX(e.i18nKey) || e.message).join('\n');

          msg.update({
            flag: 'error',
            text: 'nfce_configuration_not_validated',
            duration: 4,
          });

          setValidationError(errorMessages || translateX('validation_failed_unknown'));
        }
      } catch (error) {
        console.error('[Step4] NFCe validation error:', error);

        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error?.message ||
          translateX('validation_error_occurred');

        msg.update({
          flag: 'error',
          text: 'nfce_configuration_not_validated',
          duration: 4,
        });

        setValidationError(errorMessage);
      } finally {
        setValidating(false);
      }
    },
    [entityId, form, onNfceEnabled]
  );

  const handleOpenCscTokenModal = useCallback(() => {
    openModal(CscTokenModal, {
      uf: entityUf,
      onSuccess: ({ token, tokenMasked }) => {
        if (form) {
          form.setFieldValue(['fiscalData', 'nfce', 'csc', 'token'], token);
          form.setFieldValue(['fiscalData', 'nfce', 'csc', 'tokenMasked'], tokenMasked);
        }

        appService.message({
          flag: 's',
          text: 'csc_token_updated',
          duration: 2,
        });
      },
    });
  }, [form, entityUf]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ paddingTop: 8 }}>
      {/* Info Alert */}
      <Alert
        type="info"
        showIcon
        icon={<ShoppingCartOutlined />}
        style={{ marginBottom: 24 }}
        title={translateX('configure_nfce')}
        description={translateX('configure_nfce_description')}
      />

      {/* NFCe Card - Reusing from fiscalConfig */}
      <NfceCard
        nfceEnabled={localNfceEnabled}
        locked={false}
        validating={validating}
        validationError={validationError}
        cscLinkUrl={cscLinkUrl}
        cscLinkName={cscLinkName}
        onToggle={handleNfceToggle}
        onOpenCscTokenModal={handleOpenCscTokenModal}
      />
    </div>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const step4 = ({ entityId, company, nfceEnabled, onNfceEnabled, hidden = false }) => ({
  stepTitle: 'nfce_configuration',
  stepTooltip: 'configure_and_activate_nfce',
  hidden,
  content: ({ form }) => (
    <Step4Content
      entityId={entityId}
      company={company}
      nfceEnabled={nfceEnabled}
      onNfceEnabled={onNfceEnabled}
      form={form}
    />
  ),
  // This is the last step - button text changes to "Confirm"
  isConfirmStep: true,
  // Validation: NFCe must be enabled to complete
  onNext: async () => {
    if (!nfceEnabled) {
      const { default: appService } = await import('services/appService');
      appService.message({
        flag: 'warning',
        text: 'enable_nfce_to_complete',
        duration: 4,
      });
      return false; // Prevent completion
    }
    return true; // Allow completion
  },
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step4;
